import React, { useState, useEffect } from "react";
import {
  defaultSearchDetails,
  searchBenefits,
  timeOptions,
} from "../../data/bookingData";

// --- Data: Popular Airports for Datalist ---
const popularAirportsList = [
  "Atlanta (ATL)",
  "New York (JFK)",
  "Los Angeles (LAX)",
  "Orlando (MCO)",
  "Miami (MIA)",
  "Las Vegas (LAS)",
  "Chicago (ORD)",
  "Dallas (DFW)",
  "Denver (DEN)",
  "San Francisco (SFO)",
  "Seattle (SEA)",
  "Boston (BOS)",
  "Newark (EWR)",
  "Houston (IAH)",
  "Phoenix (PHX)",
  "Fort Lauderdale (FLL)",
  "Washington DC (IAD)",
  "San Diego (SAN)",
  "Charlotte (CLT)",
  "Detroit (DTW)",
];

// --- Icons ---
const LocationIcon = () => (
  <svg
    className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg
    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const handlePhoneClick = () => {
  if (window.gtag) {
    window.gtag("config", "AW-18343552397/3FAKCIr6l9ccEI3L8apE", {
      phone_conversion_number: "1-877-991-0909",
    });
  }
};

// --- Modal Components ---
function SearchingModal() {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#071735]/70 p-4 backdrop-blur-sm transition-opacity duration-300">
      <div className="w-full max-w-[420px] rounded-3xl bg-white px-8 py-10 text-center shadow-2xl transform scale-100 animate-in fade-in zoom-in duration-200">
        <svg
          className="mx-auto mb-6 h-12 w-12 animate-spin text-[#e32b22]"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-100"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <h2 className="mb-3 text-2xl font-extrabold text-[#091b3e]">
          Searching Available Options
        </h2>
        <p className="text-[15px] leading-relaxed text-slate-500">
          Please wait while we check available car rental options at your
          selected location.
        </p>
      </div>
    </div>
  );
}

function SuccessModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#071735]/70 p-4 backdrop-blur-sm">
      <div className="relative w-full max-w-[500px] rounded-3xl bg-white p-8 text-center shadow-2xl sm:p-10 animate-in fade-in zoom-in duration-300">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-800"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="mx-auto mb-5 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-green-50">
          <div className="flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-green-500">
            <svg
              className="h-6 w-6 text-green-500"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <h2 className="mb-4 text-2xl font-extrabold text-[#091b3e] sm:text-[28px]">
          Request Submitted
        </h2>
        <p className="mb-8 text-[15px] leading-relaxed text-slate-500">
          Thank you. Your car rental request has been received. Our support team
          will review your details and contact you shortly with available
          options.
        </p>

        <div className="mb-6 rounded-2xl border border-slate-100 bg-slate-50 py-5 shadow-inner">
          <p className="mb-1 flex items-center justify-center gap-2 text-[14px] font-bold text-[#091b3e]">
            <svg
              className="h-5 w-5 text-[#e32b22]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            Need urgent assistance?
          </p>
          <a
            href="tel:+18779910909"
            onClick={handlePhoneClick}
            className="text-[24px] sm:text-[26px] font-black text-[#e32b22] hover:text-red-700 transition-colors"
          >
            1-877-991-0909
          </a>
        </div>

        <p className="flex items-center justify-center gap-2 text-[11px] font-bold tracking-wider text-slate-400 uppercase">
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
          Secure Enquiry - No sensitive payment details
        </p>
      </div>
    </div>
  );
}

// --- Main Component ---
function RentalSearch() {
  const [isDifferentDropoff, setIsDifferentDropoff] = useState(false);
  const [searchDetails, setSearchDetails] = useState(
    defaultSearchDetails || {
      pickupLocation: "",
      dropoffLocation: "",
      pickupDate: "",
      pickupTime: "10:00",
      returnDate: "",
      returnTime: "10:00",
    },
  );

  const [isSearching, setIsSearching] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [minDate, setMinDate] = useState("");

  // Set minimum date to today's date so past dates cannot be selected
  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    setMinDate(`${yyyy}-${mm}-${dd}`);
  }, []);

  const updateSearchDetail = (name) => (event) => {
    setSearchDetails((current) => ({
      ...current,
      [name]: event.target.value,
    }));
  };

  const handleSearchClick = () => {
    setIsSearching(true);
    setTimeout(() => {
      setIsSearching(false);
      setIsSuccessModalOpen(true);
    }, 2000);
  };

  return (
    <div className="w-full max-w-7xl mx-auto font-sans">
      <div className="bg-[#091b3e] rounded-3xl shadow-2xl p-2 sm:p-4 md:p-6 lg:p-8">
        {/* === TABS SECTION === */}
        <div className="flex gap-1 md:gap-2 px-2 sm:px-4 mb-0">
          <button
            className={`flex-1 sm:flex-none px-4 sm:px-8 py-3.5 sm:py-4 rounded-t-xl text-sm sm:text-base font-bold transition-all duration-300 ${
              !isDifferentDropoff
                ? "bg-white text-[#091b3e] shadow-[0_-4px_10px_rgba(0,0,0,0.1)] z-10"
                : "bg-white/10 text-slate-300 hover:bg-white/20 hover:text-white"
            }`}
            type="button"
            onClick={() => setIsDifferentDropoff(false)}
          >
            Same drop-off
          </button>
          <button
            className={`flex-1 sm:flex-none px-4 sm:px-8 py-3.5 sm:py-4 rounded-t-xl text-sm sm:text-base font-bold transition-all duration-300 ${
              isDifferentDropoff
                ? "bg-white text-[#091b3e] shadow-[0_-4px_10px_rgba(0,0,0,0.1)] z-10"
                : "bg-white/10 text-slate-300 hover:bg-white/20 hover:text-white"
            }`}
            type="button"
            onClick={() => setIsDifferentDropoff(true)}
          >
            Different drop-off
          </button>
        </div>

        {/* Airport Datalist */}
        <datalist id="airport-options">
          {popularAirportsList.map((airport) => (
            <option value={airport} key={airport} />
          ))}
        </datalist>

        {/* === SEARCH FORM AREA === */}
        <div className="relative z-20 bg-white p-4 sm:p-5 lg:p-6 rounded-b-2xl rounded-tr-2xl shadow-xl flex flex-col lg:flex-row items-end gap-4 lg:gap-3">
          {/* Pick-Up Location */}
          <div className="w-full flex-1">
            <label className="block text-[11px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5 ml-1">
              Pick-up Location
            </label>
            <div className="relative">
              <LocationIcon />
              <input
                type="text"
                list="airport-options"
                placeholder="City, airport, or zip"
                className="w-full bg-slate-50 border border-slate-200 text-[#091b3e] rounded-xl pl-10 pr-4 py-3.5 sm:py-4 text-sm sm:text-base font-semibold focus:ring-2 focus:ring-[#e32b22] focus:border-transparent outline-none transition-all placeholder:text-slate-400 placeholder:font-normal"
                value={searchDetails.pickupLocation}
                onChange={updateSearchDetail("pickupLocation")}
              />
            </div>
          </div>

          {/* Optional Drop-Off Location */}
          {isDifferentDropoff && (
            <div className="w-full flex-1">
              <label className="block text-[11px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5 ml-1">
                Drop-off Location
              </label>
              <div className="relative">
                <LocationIcon />
                <input
                  type="text"
                  list="airport-options"
                  placeholder="City, airport, or zip"
                  className="w-full bg-slate-50 border border-slate-200 text-[#091b3e] rounded-xl pl-10 pr-4 py-3.5 sm:py-4 text-sm sm:text-base font-semibold focus:ring-2 focus:ring-[#e32b22] focus:border-transparent outline-none transition-all placeholder:text-slate-400 placeholder:font-normal"
                  value={searchDetails.dropoffLocation}
                  onChange={updateSearchDetail("dropoffLocation")}
                />
              </div>
            </div>
          )}

          {/* Pick-Up Date & Time Group */}
          <div className="w-full lg:flex-[1.1] flex gap-2">
            <div className="flex-1">
              <label className="block text-[11px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5 ml-1">
                Pick-Up Date
              </label>
              <div className="relative">
                <CalendarIcon />
                <input
                  type="date"
                  min={minDate}
                  className="w-full bg-slate-50 border border-slate-200 text-[#091b3e] rounded-xl pl-9 pr-2 py-3.5 sm:py-4 text-sm font-semibold focus:ring-2 focus:ring-[#e32b22] focus:border-transparent outline-none transition-all"
                  value={searchDetails.pickupDate}
                  onChange={updateSearchDetail("pickupDate")}
                />
              </div>
            </div>
            <div className="w-24 sm:w-28 flex-shrink-0">
              <label className="block text-[11px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5 ml-1">
                Time
              </label>
              <select
                className="w-full bg-slate-50 border border-slate-200 text-[#091b3e] rounded-xl px-2 py-3.5 sm:py-4 text-sm font-semibold focus:ring-2 focus:ring-[#e32b22] focus:border-transparent outline-none transition-all cursor-pointer"
                value={searchDetails.pickupTime}
                onChange={updateSearchDetail("pickupTime")}
              >
                {timeOptions.map((time) => (
                  <option value={time} key={`pickup-${time}`}>
                    {time}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Return Date & Time Group */}
          <div className="w-full lg:flex-[1.1] flex gap-2">
            <div className="flex-1">
              <label className="block text-[11px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5 ml-1">
                Return Date
              </label>
              <div className="relative">
                <CalendarIcon />
                <input
                  type="date"
                  min={searchDetails.pickupDate || minDate} // Return date can't be before pickup date
                  className="w-full bg-slate-50 border border-slate-200 text-[#091b3e] rounded-xl pl-9 pr-2 py-3.5 sm:py-4 text-sm font-semibold focus:ring-2 focus:ring-[#e32b22] focus:border-transparent outline-none transition-all"
                  value={searchDetails.returnDate}
                  onChange={updateSearchDetail("returnDate")}
                />
              </div>
            </div>
            <div className="w-24 sm:w-28 flex-shrink-0">
              <label className="block text-[11px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5 ml-1">
                Time
              </label>
              <select
                className="w-full bg-slate-50 border border-slate-200 text-[#091b3e] rounded-xl px-2 py-3.5 sm:py-4 text-sm font-semibold focus:ring-2 focus:ring-[#e32b22] focus:border-transparent outline-none transition-all cursor-pointer"
                value={searchDetails.returnTime}
                onChange={updateSearchDetail("returnTime")}
              >
                {timeOptions.map((time) => (
                  <option value={time} key={`return-${time}`}>
                    {time}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Search Button */}
          <div className="w-full lg:w-auto mt-2 lg:mt-0 flex-shrink-0">
            <button
              onClick={handleSearchClick}
              className="w-full lg:w-auto bg-[#e32b22] hover:bg-red-700 text-white font-black py-4 sm:py-4 px-8 rounded-xl transition-all shadow-lg shadow-red-900/30 whitespace-nowrap h-[54px] sm:h-[58px] flex items-center justify-center gap-2 transform active:scale-95"
              type="button"
            >
              <svg
                className="w-5 h-5 hidden lg:block"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="3"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              Search Cars
            </button>
          </div>
        </div>

        {/* Benefits Footer (Desktop Only) */}
        {searchBenefits && searchBenefits.length > 0 && (
          <div className="hidden lg:flex flex-wrap items-center gap-x-8 gap-y-3 px-6 pt-5 pb-1 text-sm font-medium text-slate-300">
            {searchBenefits.map((benefit) => (
              <span key={benefit} className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-[#e32b22]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {benefit}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {isSearching && <SearchingModal />}
      {isSuccessModalOpen && (
        <SuccessModal onClose={() => setIsSuccessModalOpen(false)} />
      )}
    </div>
  );
}

export default RentalSearch;
